import React, {useState, useCallback, useEffect} from 'react'
import {
  EXPENSE_OPTIONS,
  IMaintenanceModel,
  LIST_OPTIONS,
  EXTRA_OPTIONS,
  EXTRA_OPTIONS2,
} from './ServiceMaintenanceModel'
import DataTable, {TableAction, TableColumn} from '../../../../shared/components/DataTable'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import CheckboxInput from '../../../../shared/components/CheckboxInput' 
import AsyncSearchInput from '../../../../shared/components/AsyncSearchInput'
import {AsyncCarModel, AsyncCustomerModel} from '../../../../shared/models/ApiModels'
import {fetchAsyncCarData, fetchAsyncCustomerData} from '../../../../shared/models/SharedApiLookups'

interface Option {
  value: string
  label: string
}

type Props = {
  data: IMaintenanceModel[]
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onEdit?: (maintenance: IMaintenanceModel) => void
  onDelete?: (maintenance: IMaintenanceModel) => void
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: string, order: 'asc' | 'desc') => void
  loading?: boolean
}

export const MaintenanceTable: React.FC<Props> = ({
  data,
  currentPage,
  totalPages,
  onPageChange,
  onEdit,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
  loading = false,
}) => {
  // Filtreleme state'leri
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [expenseType, setExpenseType] = useState('')
  const [searchText, setSearchText] = useState('')
  const [showRefund, setShowRefund] = useState(false)
  const [periodicService, setPeriodicService] = useState(false)
  const [damageAccident, setDamageAccident] = useState(false)
  const [mechanicalFailure, setMechanicalFailure] = useState(false)
  const [maintenance, setMaintenance] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [hideRental, setHideRental] = useState(false)
  const [customer, setCustomer] = useState<Option | null>(null)
  const [car, setCar] = useState<Option | null>(null)

  // Şube seçenekleri için state
  const [branchesOptions, setBranchesOptions] = useState<{value: string; label: string}[]>([])
  const [branchesLoading, setBranchesLoading] = useState(false)

  const maintenanceColumns: TableColumn<IMaintenanceModel>[] = [
    {key: 'expenseName', label: 'Gider Adı', className: 'fw-semibold', sortable: true},
    {key: 'customerId', label: 'Cari Bilgi'},
    {key: 'plate', label: 'Plaka', sortable: true},
    {key: 'brand', label: 'Marka'},
    {key: 'type', label: 'Tipi'},
    {key: 'fuelType', label: 'Yakıt Türü'},
    {key: 'gear', label: 'Vites'},
  ]

  const maintenanceActions: TableAction<IMaintenanceModel>[] = [
    {
      label: 'Düzenle',
      icon: 'bi bi-pencil',
      className: 'btn-light-primary me-2',
      onClick: (maintenance) => onEdit?.(maintenance),
    },
    {
      label: 'Sil',
      icon: 'bi bi-trash',
      className: 'btn-light-danger',
      onClick: (maintenance) => onDelete?.(maintenance),
    },
  ]

  // Şube verilerini API'den çekme
  const fetchBranches = useCallback(async () => {
    setBranchesLoading(true)
    try {
      const fetchedBranches = await fetch('/api/definition/branches').then((res) => res.json())

      const options = fetchedBranches.map((b: any) => ({
        value: b.id.toString(),
        label: b.name,
      }))
      setBranchesOptions(options)
    } catch (err) {
      console.error('Error fetching branches:', err)
    } finally {
      setBranchesLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBranches()
  }, [fetchBranches])

  return (
    <div className='card'>
      <div className='card-header'>
        <h3 className='card-title'>SERVİS & BAKIM LİSTESİ</h3>
        <div className='card-toolbar'></div>
      </div>
      <div className='card-body'>
        {/* Filtreleme Bölümü */}
        <div className='row mb-6'>
          {/* Cari Ara */}
          <div className='col-md-6'>
            <label className='form-label'>Cari Ara</label>
            <AsyncSearchInput<AsyncCustomerModel>
              name="customerId"
              label='Cari Ara'
              placeholder='Ad Soyad girin...'
              fetchHandler={fetchAsyncCustomerData}
              displayField={(item) => `${item.firstName} ${item.lastName}`}
              onSelect={(item) => console.log('Seçilen cari:', item)}
              onClear={() => console.log('Cari temizlendi')}
            />
          </div>

          {/* Araç Ara */}
          <div className='col-md-6'>
            <label className='form-label'>Araç Ara</label>
            <AsyncSearchInput<AsyncCarModel>
              name="carId"
              label='Araç Ara'
              placeholder='Araç bilgisi girin...'
              fetchHandler={fetchAsyncCarData}
              displayField={(item) => `${item.plate} (${item.modelYear})`}
              onSelect={(item) => console.log('Seçilen araç:', item)}
              onClear={() => console.log('Araç temizlendi')}
            />
          </div>
        </div>

        <div className='row mb-6'>
          <SelectInput name='dateId' label='' wrapperClassName='col-md-2' options={LIST_OPTIONS} />
          <TextInput
            name='startDate'
            label='Başlangıç Tarihi'
            type='date'
            wrapperClassName='col-md-2'
          />
          <TextInput name='endDate' label='Bitiş Tarihi' type='date' wrapperClassName='col-md-2' />
          <SelectInput
            name='expenseType'
            label='Gider Türü'
            wrapperClassName='col-md-6'
            options={EXPENSE_OPTIONS}
          />
        </div>

        <div className='row mb-6'>
          <SelectInput
            name='branchId'
            label='İşlem Şube'
            wrapperClassName='col-md-6'
            options={branchesOptions}
          />
          <CheckboxInput
            name='periodicService'
            label='Periyodik Bakım'
            wrapperClassName='col-md-3'
          />
          <CheckboxInput name='damageAccident' label='Hasar / Kaza' wrapperClassName='col-md-3' />
          <CheckboxInput name='expenseName' label='Gider İade' wrapperClassName='col-md-6' />
          <SelectInput name='Type' label='' wrapperClassName='col-md-3' options={EXTRA_OPTIONS} />
          <SelectInput name='Type2' label='' wrapperClassName='col-md-3' options={EXTRA_OPTIONS2} />
        </div>

        <div className='row mb-6'>
          <div className='col-md-2'>
            <button type='button' className='btn btn-primary'>
              Filtrele
            </button>
          </div>
          <div className='col-md-6'>
            <span className='text-muted'>Kayıt Sayısı: {data.length}</span>
          </div>
        </div>

        {/* Tablo */}
        <DataTable
          data={data}
          columns={maintenanceColumns}
          actions={maintenanceActions}
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          loading={loading}
          emptyMessage='Henüz bakım bilgisi bulunamadı'
        />
      </div>
    </div>
  )
}
